import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';

interface TenorGif {
  id: string;
  media_formats?: {
    tinygif?: { url?: string };
    gif?: { url?: string };
  };
}

export interface TenorGifDialogProps {
  onClose: () => void;
  onSelectGif: (gifUrl: string) => void;
}

const DEFAULT_TENOR_API_KEY = 'AIzaSyA8WihhjU7-RxpZOa14LRwtgQXR3GRjbAA';

const TenorGifDialog = ({ onClose, onSelectGif }: TenorGifDialogProps) => {
  const [gifQuery, setGifQuery] = useState('');
  const [debouncedGifQuery, setDebouncedGifQuery] = useState('');
  const [isGifLoading, setIsGifLoading] = useState(false);
  const [gifSearchError, setGifSearchError] = useState('');
  const [gifResults, setGifResults] = useState<TenorGif[]>([]);

  const tenorApiKey = (
    (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
      ?.VITE_TENOR_API_KEY ?? DEFAULT_TENOR_API_KEY
  ).trim();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedGifQuery(gifQuery.trim());
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [gifQuery]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const abortController = new AbortController();
    const endpoint = debouncedGifQuery
      ? 'https://tenor.googleapis.com/v2/search'
      : 'https://tenor.googleapis.com/v2/featured';
    const params = new URLSearchParams({
      key: tenorApiKey,
      limit: '20',
      media_filter: 'tinygif,gif',
      contentfilter: 'medium',
      locale: 'en_US',
      client_key: 'dotvoting-board',
    });
    if (debouncedGifQuery) {
      params.set('q', debouncedGifQuery);
    }

    setIsGifLoading(true);
    setGifSearchError('');

    fetch(`${endpoint}?${params.toString()}`, { signal: abortController.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to load GIFs');
        }

        const payload = (await response.json()) as { results?: TenorGif[] };
        setGifResults(payload.results ?? []);
      })
      .catch((error: Error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setGifSearchError(error.message || 'Failed to load GIFs');
        setGifResults([]);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsGifLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [debouncedGifQuery, open, tenorApiKey]);

  const handleClose = () => {
    setGifQuery('');
    setDebouncedGifQuery('');
    setGifSearchError('');
    setGifResults([]);
    onClose();
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        maxHeight: 360,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={1}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search GIFs..."
          value={gifQuery}
          onChange={(event) => setGifQuery(event.target.value)}
        />
        <IconButton size="small" onClick={handleClose} aria-label="Close GIF picker">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, position: 'relative', overflowY: 'auto' }}>
        {gifSearchError && (
          <Typography variant="body2" color="error" mb={2}>
            {gifSearchError}
          </Typography>
        )}

        {isGifLoading && (
          <Stack alignItems="center" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!isGifLoading && !gifSearchError && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: 1,
            }}
          >
            {gifResults.map((gif) => {
              const previewUrl = gif.media_formats?.tinygif?.url ?? gif.media_formats?.gif?.url;
              const imageUrl = gif.media_formats?.gif?.url ?? previewUrl;

              if (!previewUrl || !imageUrl) {
                return null;
              }

              return (
                <Box
                  key={gif.id}
                  component="button"
                  type="button"
                  onClick={() => {
                    onSelectGif(imageUrl);
                    handleClose();
                  }}
                  sx={{
                    border: 0,
                    p: 0,
                    m: 0,
                    cursor: 'pointer',
                    borderRadius: 1,
                    overflow: 'hidden',
                    lineHeight: 0,
                    backgroundColor: 'transparent',
                  }}
                >
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="GIF option"
                    sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TenorGifDialog;
